/**
 * BooleanRenderer - Minimal inline toggle for boolean settings
 *
 * Clean design: label on left, toggle on right
 */

import React from 'react';
import { makeStyles, tokens, Text, Switch } from '@fluentui/react-components';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    minHeight: '44px',
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
});

export interface BooleanRendererProps extends SettingRendererProps<boolean> {
  /** Label is required for boolean renderers */
  label: string;
}

/**
 * BooleanRenderer component for toggle settings
 */
export const BooleanRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label,
}: BooleanRendererProps) => {
  const styles = useStyles();

  // Don't render if setting is not supported
  if (!setting.isSupported) {
    return null;
  }

  const handleChange = (_: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
    onChange(data.checked);
  };

  return (
    <div className={styles.container}>
      <Text className={styles.label}>{label}</Text>
      <Switch
        checked={setting.value ?? false}
        onChange={handleChange}
        disabled={readOnly}
        aria-label={label}
      />
    </div>
  );
};

BooleanRenderer.displayName = 'BooleanRenderer';

// Register this renderer - cast needed because BooleanRenderer requires label
registerSettingRenderer('boolean', BooleanRenderer as React.ComponentType<SettingRendererProps<boolean>>);
